<script setup>
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Button from 'dashboard/components-next/button/Button.vue';
import ProgressBar from 'dashboard/components-next/progress/ProgressBar.vue';

const props = defineProps({
  // faq_stats payload ({ coverage, approved, documents }); undefined while loading.
  knowledge: {
    type: Object,
    default: undefined,
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

const assistantId = computed(() => Number(route.params.assistantId));
const getAssistant = useMapGetter('captainAssistants/getRecord');
const assistant = computed(() => getAssistant.value(assistantId.value));
const captainInboxes = useMapGetter('captainInboxes/getRecords');
const inboxUiFlags = useMapGetter('captainInboxes/getUIFlags');

const hasCaptainV2 = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CAPTAIN_V2)
);

watch(
  assistantId,
  id => {
    if (!id) return;
    store.dispatch('captainInboxes/get', { assistantId: id });
    if (!assistant.value?.id) store.dispatch('captainAssistants/show', id);
  },
  { immediate: true }
);

const hasKnowledge = computed(
  () =>
    (props.knowledge?.documents ?? 0) > 0 ||
    (props.knowledge?.approved ?? 0) > 0
);

const hasBehavior = computed(
  () =>
    (assistant.value?.response_guidelines?.length ?? 0) +
      (assistant.value?.guardrails?.length ?? 0) >
    0
);

const hasInbox = computed(() => captainInboxes.value.length > 0);

const items = computed(() => {
  const list = [
    { key: 'CREATE', done: true },
    {
      key: 'KNOWLEDGE',
      done: hasKnowledge.value,
      route: 'captain_assistants_documents_index',
    },
  ];
  // Guidelines/guardrails only exist as pages on Captain V2 accounts.
  if (hasCaptainV2.value) {
    list.push({
      key: 'BEHAVIOR',
      done: hasBehavior.value,
      route: 'captain_assistants_guidelines_index',
    });
  }
  list.push({
    key: 'INBOX',
    done: hasInbox.value,
    route: 'captain_assistants_inboxes_index',
  });
  return list;
});

const doneCount = computed(() => items.value.filter(item => item.done).length);

// Wait for every data source (assistant, inboxes, faq stats) before showing,
// so a fully-configured assistant never flashes a false pending item.
const showChecklist = computed(
  () =>
    Boolean(assistant.value?.id) &&
    !inboxUiFlags.value.fetchingList &&
    props.knowledge !== undefined &&
    doneCount.value < items.value.length
);

const goToItem = item => {
  if (!item.route) return;
  router.push({
    name: item.route,
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
    },
  });
};
</script>

<template>
  <div
    v-if="showChecklist"
    class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
  >
    <div
      class="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-n-weak"
    >
      <div class="min-w-0">
        <h3 class="mb-0 text-base font-semibold text-n-slate-12">
          {{ t('CAPTAIN.OVERVIEW.CHECKLIST.TITLE') }}
        </h3>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('CAPTAIN.OVERVIEW.CHECKLIST.NOTE') }}
        </p>
      </div>
      <div class="flex items-center gap-3 ltr:ml-auto rtl:mr-auto">
        <span class="text-sm text-n-slate-11">
          {{
            t('CAPTAIN.OVERVIEW.CHECKLIST.PROGRESS', {
              done: doneCount,
              total: items.length,
            })
          }}
        </span>
        <ProgressBar
          :value="doneCount"
          :max="items.length"
          :show-value="false"
          size="sm"
          class="w-24"
        />
      </div>
    </div>
    <ul class="flex flex-col pl-0 mb-0 list-none divide-y divide-n-weak">
      <li
        v-for="item in items"
        :key="item.key"
        class="flex items-center gap-3 px-5 py-3"
      >
        <span
          class="grid rounded-full size-5 place-items-center"
          :class="
            item.done
              ? 'bg-n-teal-9 text-white'
              : 'border border-dashed border-n-slate-8'
          "
        >
          <span v-if="item.done" class="i-lucide-check size-3" />
        </span>
        <span
          class="text-sm"
          :class="
            item.done ? 'text-n-slate-11 line-through' : 'text-n-slate-12'
          "
        >
          {{ t(`CAPTAIN.OVERVIEW.CHECKLIST.ITEMS.${item.key}`) }}
        </span>
        <Button
          v-if="!item.done && item.route"
          link
          sm
          type="button"
          trailing-icon
          icon="i-lucide-arrow-right"
          :label="t(`CAPTAIN.OVERVIEW.CHECKLIST.ACTIONS.${item.key}`)"
          class="ltr:ml-auto rtl:mr-auto"
          @click="goToItem(item)"
        />
      </li>
    </ul>
  </div>
</template>
